import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';

/**
 * Account settings.
 *
 * Currently one thing: the password. This is also where the "forgotten
 * password" journey ends — sign in with an emailed link, then set a new one
 * here — which is why the app has no separate reset flow to build, secure and
 * rate-limit.
 */
export function Settings() {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < 10;
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready = password.length >= 10 && confirm === password;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await api.setPassword(password);
      setPassword('');
      setConfirm('');
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <h1 className="question">Settings</h1>

      <div className="card">
        <h2>Account</h2>
        <div className="meta" style={{ marginBottom: 0 }}>
          <span>Signed in as {me.data?.displayName ?? '…'}</span>
          {me.data?.isAdmin && <span className="badge open">administrator</span>}
        </div>
      </div>

      <div className="card">
        <h2>Password</h2>
        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="new-password">New password</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                id="new-password"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 10 characters"
              />
              <button
                type="button"
                style={{ padding: '5px 10px', fontSize: 13, whiteSpace: 'nowrap' }}
                onClick={() => setShow(!show)}
              >
                {show ? 'Hide' : 'Show'}
              </button>
            </div>
            {tooShort && (
              <div className="hint" style={{ color: 'var(--bad)' }}>
                {10 - password.length} more character{10 - password.length === 1 ? '' : 's'} needed.
              </div>
            )}
          </div>

          <div className="field">
            <label htmlFor="confirm-password">Repeat it</label>
            <input
              id="confirm-password"
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {mismatch && <div className="hint" style={{ color: 'var(--bad)' }}>These do not match.</div>}
          </div>

          <button className="submit" data-side="YES" disabled={!ready || busy}>
            {busy ? 'Saving…' : 'Change password'}
          </button>
          {error && <div className="notice error">{error}</div>}
          {done && <div className="notice ok">Password changed. Other devices have been signed out.</div>}
        </form>

        <p style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55, marginTop: 14, marginBottom: 0 }}>
          Changing your password signs out every other device but keeps you signed in here. If you
          ever forget it, sign in with an emailed link and come back to this page.
        </p>
      </div>
    </div>
  );
}
